import { lazy, Suspense, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import ctrLogoAsset from "@/assets/ctr-logo.webp.asset.json";
import { brand } from "@/data/site/brand";
import { primaryNav, type DropdownKey } from "@/data/site/navigation";
import { SiteServicesDropdown } from "@/components/site/SiteServicesDropdown";
import { SiteAboutDropdown } from "@/components/site/SiteAboutDropdown";
import { SiteResourcesDropdown } from "@/components/site/SiteResourcesDropdown";
import { SiteServiceAreasDropdown } from "@/components/site/SiteServiceAreasDropdown";
// Mobile menu is lazy-loaded — it only renders once the user taps the
// hamburger, so it stays out of the initial bundle on desktop and mobile.
const SiteMobileMenu = lazy(() =>
  import("@/components/site/SiteMobileMenu").then((m) => ({ default: m.SiteMobileMenu })),
);

export const SiteHeader = () => {
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close on route change
  useEffect(() => {
    setActiveDropdown(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (mobileOpen) {
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    } else {
      html.style.overflow = "";
      body.style.overflow = "";
    }
    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-24">
            <Link to="/" className="flex items-center shrink-0" onClick={() => setActiveDropdown(null)}>
              <img
                src={ctrLogoAsset.url}
                alt="Comprehensive Tax Resolution"
                className="h-12 lg:h-20 w-auto"
               width={250} height={150} />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center h-full">
              {primaryNav.map((item) => {
                const hasDropdown = !!item.dropdown;
                const isOpen = hasDropdown && activeDropdown === item.dropdown;
                return (
                  <div
                    key={item.name}
                    className="group flex h-24 items-center"
                    onMouseEnter={() => setActiveDropdown(item.dropdown ?? null)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <NavLink
                      to={item.href}
                      aria-haspopup={hasDropdown ? "menu" : undefined}
                      aria-expanded={hasDropdown ? isOpen : undefined}
                      className={({ isActive }) =>
                        `relative inline-flex h-full items-center gap-1 px-4 text-[15px] lg:text-base font-semibold tracking-tight transition-colors after:content-[''] after:absolute after:left-4 after:right-4 after:bottom-[18px] after:h-[2px] after:bg-gold after:origin-left after:transition-transform after:duration-200 ${
                          isActive || isOpen
                            ? "text-navy-dark after:scale-x-100"
                            : "text-navy-dark hover:text-navy after:scale-x-0 hover:after:scale-x-100"
                        }`
                      }
                    >
                      {item.name}
                      {hasDropdown && (
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-200 ${
                            isOpen ? "rotate-180 text-gold" : ""
                          }`}
                        />
                      )}
                    </NavLink>

                    {hasDropdown && isOpen && (
                      <div
                        className="fixed left-0 right-0 top-16 lg:top-24 opacity-100 visible pointer-events-auto"
                        onMouseEnter={() => setActiveDropdown(item.dropdown ?? null)}
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        {item.dropdown === "services" && (
                          <SiteServicesDropdown onClose={() => setActiveDropdown(null)} />
                        )}
                        {item.dropdown === "serviceAreas" && (
                          <SiteServiceAreasDropdown onClose={() => setActiveDropdown(null)} />
                        )}
                        {item.dropdown === "resources" && (
                          <SiteResourcesDropdown onClose={() => setActiveDropdown(null)} />
                        )}
                        {item.dropdown === "about" && (
                          <SiteAboutDropdown onClose={() => setActiveDropdown(null)} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-2">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold text-base px-6 py-3 rounded transition-colors"
              >
                <Phone className="w-4 h-4" /> {brand.phone}
              </a>
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center min-h-11 min-w-11 -mr-2 rounded text-navy hover:bg-navy/5 active:bg-navy/10 transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="site-mobile-menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <Suspense fallback={null}>
          <SiteMobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
        </Suspense>
      )}
    </>
  );
};
